import { useEffect, useState } from "react";
import Table from "@/components/Table/Table";
import type { Column } from "@/types/common";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getSupportItems } from "@/store/slices/supportSlice";
import SupportService from "@/services/api/support";
import type { SupportItem } from "@/types/support";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import { TableCellTooltip } from "@/components/Tooltip";
import toast from "@/lib/toast";
import { formatDate as formatDateBase } from "@/lib/dateFormat";
import SupportFormModal from "./components/SupportFormModal/SupportFormModal";
import type { SupportFormValues } from "./components/SupportFormModal/SupportFormModal";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import Button from "@/components/Button/Button";
import Pagination from "@/components/Table/Pagination";
import SearchIcon from "@/components/SVGComponents/SearchIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import SupportCard from "./components/SupportCard";
import styles from "./Support.module.scss";

// Same "-" empty-fallback and "show the raw string rather than a generic
// placeholder if it doesn't parse" behavior as before — passed through to
// the shared formatter instead of a local Intl.DateTimeFormat + try/catch.
const formatDate = (iso?: string) => formatDateBase(iso, { day: "numeric", month: "long", fallback: iso || "-" });

const SupportPage = () => {
  const dispatch = useAppDispatch();
  const { items: supportItems, totalItems, isFetching } = useAppSelector((state) => state.support);

  const [search, setSearch] = useState("");
  const [pageSelected, setPageSelected] = useState(1);
  const [rowsPerPageValue] = useState(10);
  const debouncedSearch = useDebouncedValue(search, 300);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingSupportItem, setEditingSupportItem] = useState<SupportItem | null>(null);
  const [deletingSupportItem, setDeletingSupportItem] = useState<SupportItem | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  usePageSubtitle(`${totalItems} question${totalItems === 1 ? "" : "s"} published`);

  useEffect(() => {
    setPageSelected(1);
  }, [debouncedSearch]);

  useEffect(() => {
    dispatch(
      getSupportItems({
        search: debouncedSearch,
        pageNo: pageSelected,
        perPage: rowsPerPageValue,
      }),
    );
  }, [dispatch, debouncedSearch, pageSelected, rowsPerPageValue]);

  const handleAddClick = () => {
    setEditingSupportItem(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (item: SupportItem) => {
    setEditingSupportItem(item);
    setIsFormModalOpen(true);
  };

  const handleFormModalClose = () => {
    setIsFormModalOpen(false);
    setEditingSupportItem(null);
  };

  const handleFormSubmit = async (values: SupportFormValues) => {
    try {
      if (editingSupportItem) {
        const res = await SupportService.updateSupportItem(editingSupportItem.id, values);
        if (res?.success) {
          toast.success(res.message || "Support item updated successfully.");
        }
      } else {
        const res = await SupportService.createSupportItem(values);
        if (res?.success) {
          toast.success(res.message || "Support item created successfully.");
        }
      }
      handleFormModalClose();
      dispatch(
        getSupportItems({
          search: debouncedSearch,
          pageNo: pageSelected,
          perPage: rowsPerPageValue,
        }),
      );
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSupportItem) return;
    try {
      const res = await SupportService.deleteSupportItem(deletingSupportItem.id);
      if (res?.success) {
        toast.success(res.message || "Support item deleted successfully.");
      }
      setDeletingSupportItem(null);
      dispatch(
        getSupportItems({
          search: debouncedSearch,
          pageNo: pageSelected,
          perPage: rowsPerPageValue,
        }),
      );
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const columns: Column<SupportItem>[] = [
    {
      name: "Question",
      width: "26%",
      grow: 0,
      cell: (row) => <TableCellTooltip value={row?.question || "—"} copyable />,
    },
    {
      name: "Answer",
      width: "22%",
      grow: 0,
      cell: (row) => <TableCellTooltip value={row?.answer || "—"} maxLines={2} copyable />,
    },
    { name: "Created on", width: "13%", grow: 0, selector: (row) => formatDate(row?.createdAt) },
    { name: "Created by", width: "11%", grow: 0, selector: (row) => row?.createdByUser || "—" },
    { name: "Updated on", width: "10%", grow: 0, selector: (row) => formatDate(row?.updatedAt) },
    { name: "Updated by", width: "10%", grow: 0, selector: (row) => row?.updatedByUser || "—" },
    {
      name: "Action",
      width: "8%",
      grow: 0,
      cell: (row) => (
        <div className={styles.actionCell}>
          <button
            type="button"
            className={styles.editButton}
            aria-label={`Edit "${row.question}"`}
            onClick={() => handleEditClick(row)}
          >
            <PencilIcon />
          </button>
          <button
            type="button"
            className={styles.deleteButton}
            aria-label={`Delete "${row.question}"`}
            onClick={() => setDeletingSupportItem(row)}
          >
            <DeleteIcon width={28} height={28} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Desktop View (Preserved exactly as existing) */}
      <div className={styles.desktopView}>
        <Table<SupportItem>
          columns={columns}
          data={supportItems}
          progressPending={isFetching}
          header={{
            search,
            setSearch,
            searchPlaceholder: "Search support",
            onExport: () => setIsExportModalOpen(true),
            addButtonTitle: "ADD SUPPORT ITEM",
            onAddClick: handleAddClick,
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalItems,
            rowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View (Clean, responsive card-based UI <= 768px only) */}
      <div className={styles.mobileView}>
        {/* Mobile Toolbar: Search Box & Actions */}
        <div className={styles.mobileToolbar}>
          <div className={styles.mobileSearchBox}>
            <SearchIcon className={styles.mobileSearchIcon} />
            <input
              type="text"
              placeholder="Search support"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <CrossIcon
                onClick={() => setSearch("")}
                withBg={false}
                height={12}
                width={12}
                strokeWidth={2}
                className={styles.mobileCrossIcon}
              />
            )}
          </div>

          <div className={styles.mobileActionsRow}>
            <Button
              outlined
              onClick={() => setIsExportModalOpen(true)}
              className={styles.mobileExportBtn}
            >
              <DownloadIcon />
              <span>EXPORT CSV</span>
            </Button>

            <Button
              onClick={handleAddClick}
              className={styles.mobileAddBtn}
            >
              <PlusIcon width={13} height={13} />
              <span>ADD SUPPORT ITEM</span>
            </Button>
          </div>
        </div>

        {/* Mobile Card List */}
        <div className={styles.mobileCardList}>
          {isFetching ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonBadge} />
                  <div className={styles.skeletonLineShort} />
                </div>
                <div className={styles.skeletonBox} />
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                </div>
              </div>
            ))
          ) : supportItems.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No support items found</h4>
              <p className={styles.mobileEmptyCaption}>
                {search
                  ? "No support items match your search query."
                  : "No support items published yet."}
              </p>
            </div>
          ) : (
            supportItems.map((item) => (
              <SupportCard
                key={item.id}
                item={item}
                onEdit={handleEditClick}
                onDelete={setDeletingSupportItem}
                formatDate={formatDate}
              />
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        {totalItems > rowsPerPageValue && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={pageSelected}
              totalCount={totalItems}
              rowsPerPageValue={rowsPerPageValue}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <SupportFormModal
        isOpen={isFormModalOpen}
        onClose={handleFormModalClose}
        supportItem={editingSupportItem}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDeleteModal
        isOpen={deletingSupportItem !== null}
        onClose={() => setDeletingSupportItem(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Support"
        message="Are you sure you want to delete this Support item?"
        className={styles.supportDeleteModal}
      />

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="faqs"
        fetchExportFile={() => SupportService.exportSupportItems({ search: debouncedSearch })}
      />
    </>
  );
};

export default SupportPage;
